import { isWeekend } from "date-fns";
import { call, select } from "redux-saga/effects";

import LeaderboardMetricFieldName from "@mapmycustomers/shared/enum/fieldModel/LeaderboardMetricFieldName";
import PlatformFilterOperator from "@mapmycustomers/shared/enum/PlatformFilterOperator";
import { Activity } from "@mapmycustomers/shared/types/entity";
import LeaderboardItem from "@mapmycustomers/shared/types/entity/LeaderboardItem";
import Organization from "@mapmycustomers/shared/types/Organization";
import Team from "@mapmycustomers/shared/types/Team";
import User from "@mapmycustomers/shared/types/User";
import ListResponse from "@mapmycustomers/shared/types/viewModel/ListResponse";

import { getDatesRange } from "@app/enum/dashboard/dateRanges";
import ActivitiesLoggedCardData from "@app/scene/dashboard/store/cards/activitiesLogged/ActivitiesLoggedCardData";
import { callApi } from "@app/store/api/callApi";
import { getOrganizationId } from "@app/store/iam";
import { getTeamsOfEntireOrganization, getUsersOfEntireOrganization } from "@app/store/members";
import ActivitiesLoggedCardConfiguration, {
  ActivitiesLoggedAggregationType,
} from "@app/types/dashboard/cards/activitiesLogged/ActivitiesLoggedCardConfiguration";
import Scope from "@app/types/dashboard/Scope";
import AggregatedListResponse from "@app/types/viewModel/AggregatedListResponse";
import { doesWeekStartFromMonday, getLocalTimeZone } from "@app/util/dates";
import { parseApiDate } from "@app/util/parsers";

import { DateAggregationItem, granularityByDateRange } from "./common";

export function* fetchActivitiesLoggedCardDataHelper(params: {
  callback: (data: ActivitiesLoggedCardData) => void;
  configuration: ActivitiesLoggedCardConfiguration;
  scope?: Scope;
}) {
  const { callback, configuration, scope } = params;
  const { criteria, dateRange, preferences } = configuration;
  const isTeamScope = !!scope?.teamId && !scope?.userId;

  const orgId: Organization["id"] = yield select(getOrganizationId);

  const granularity = granularityByDateRange[dateRange.range];
  const range = getDatesRange(dateRange.range, dateRange.subRange);

  const allOrgUsers: User[] = yield select(getUsersOfEntireOrganization);
  const teams: Team[] = yield select(getTeamsOfEntireOrganization);
  const scopeTeam = isTeamScope ? teams.find(({ id }) => id === scope.teamId) : undefined;
  const assigneeIds = scope?.userId ? [scope.userId] : scopeTeam?.users.map(({ id }) => id);

  const filters = {
    assigneeId: assigneeIds ? { [PlatformFilterOperator.CONTAINS]: assigneeIds } : undefined,
    completedAt: { $gte: range.startDate.toISOString(), $lte: range.endDate.toISOString() },
    crmActivityTypeId: criteria.activityTypeIds.length
      ? { $in: criteria.activityTypeIds }
      : undefined,
    source: criteria.sources.length ? { $in: criteria.sources } : undefined,
  };

  const payload = {
    $aggs: preferences.showChart
      ? {
          count: ["id"],
          primaryGroup: {
            field: "completedAt",
            interval: granularity,
            offset: granularity !== "week" || doesWeekStartFromMonday() ? undefined : "-1 day",
            timezone: getLocalTimeZone(),
          },
        }
      : undefined,
    $filters: filters,
    $limit: 0,
  };

  const response: AggregatedListResponse<Activity, DateAggregationItem[] | undefined> =
    yield callApi("fetchActivities", orgId, payload);

  // scope size is either equal to the assigneeIds length if Scope if User or Team,
  // or to the number of users in the organization
  const scopeSize = assigneeIds?.length ?? allOrgUsers.length;

  let sum = 0;
  const result: ActivitiesLoggedCardData = {
    count:
      response.total /
      (criteria.aggregationType === ActivitiesLoggedAggregationType.AVG ? scopeSize : 1),
    history: response.aggregations?.map((item) => {
      sum += item.doc_count;
      return {
        count:
          criteria.aggregationType === ActivitiesLoggedAggregationType.AVG
            ? item.doc_count / scopeSize
            : preferences.cumulative
              ? sum
              : item.doc_count,
        date: parseApiDate(item.key),
      };
    }),
  };

  if (preferences.showTrend) {
    // fetch again, but for the previous range
    const previousRange = getDatesRange(dateRange.range, dateRange.subRange, true);
    const payload = {
      // we need to get previous period's chart data to show it when user hovers over the trend text
      $aggs: preferences.showChart
        ? {
            count: ["id"],
            primaryGroup: {
              field: "completedAt",
              interval: granularity,
              offset: granularity !== "week" || doesWeekStartFromMonday() ? undefined : "-1 day",
              timezone: getLocalTimeZone(),
            },
          }
        : undefined,
      $filters: {
        ...filters,
        completedAt: {
          $gte: previousRange.startDate.toISOString(),
          $lte: previousRange.endDate.toISOString(),
        },
      },
      $limit: 0,
    };
    const response: AggregatedListResponse<Activity, DateAggregationItem[] | undefined> =
      yield callApi("fetchActivities", orgId, payload);
    result.previousCount =
      response.total /
      (criteria.aggregationType === ActivitiesLoggedAggregationType.AVG ? scopeSize : 1);
    if (preferences.showChart) {
      let sum = 0;
      result.previousHistory = response.aggregations?.map((item) => {
        sum += item.doc_count;
        return {
          count:
            criteria.aggregationType === ActivitiesLoggedAggregationType.AVG
              ? item.doc_count / scopeSize
              : preferences.cumulative
                ? sum
                : item.doc_count,
          date: parseApiDate(item.key),
        };
      });
    }
  }

  const filteredHistory = result.history?.filter(
    ({ count, date }, index) =>
      !isWeekend(date) || count || (result.previousHistory && result.previousHistory[index]?.count)
  );

  const filteredPreviousHistory = result.previousHistory?.filter(
    ({ count }, index) =>
      count ||
      (result.history && (result.history[index]?.count || !isWeekend(result.history[index]?.date)))
  );

  result.history = filteredHistory;
  result.previousHistory = filteredPreviousHistory;
  // if we should show the rank and scope is not the Entire Org
  if (preferences.showRankPosition && (scope?.teamId || scope?.userId)) {
    const response: ListResponse<LeaderboardItem> = yield callApi(
      "fetchLeaderboardData",
      orgId,
      {
        $and: [
          ...(criteria.activityTypeIds.length
            ? [{ crmActivityTypeId: { $in: criteria.activityTypeIds } }]
            : []),
        ],
        date: { $gte: range.startDate.toISOString(), $lte: range.endDate.toISOString() },
        includePerformanceMetric: true,
        property: { $in: [LeaderboardMetricFieldName.ACTIVITIES_COMPLETED] },
        summary: isTeamScope ? "team" : "user",
      },
      undefined
    );
    const rank = isTeamScope
      ? response.data.findIndex(({ teamId }) => teamId === scope!.teamId)
      : response.data.findIndex(({ userId }) => userId === scope!.userId);
    result.rank = rank >= 0 ? rank + 1 : undefined;
  }

  yield call(callback, result);
}
